import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from './api';

/**
 * Sesión de staff Priviax. Persiste en **sessionStorage**: sobrevive a
 * recargas de la pestaña (F5), pero se pierde al cerrarla. La expiración
 * por inactividad la controla el JWT corto (2 h) emitido por el backend.
 */
const StaffAuthContext = createContext(null);

const SS_KEY = 'priviax_staff_token';

let _memToken = null;
try { _memToken = sessionStorage.getItem(SS_KEY); } catch (_e) { /* ignore */ }
const _listeners = new Set();
const _setMemToken = (t) => {
  _memToken = t || null;
  try {
    if (_memToken) sessionStorage.setItem(SS_KEY, _memToken);
    else sessionStorage.removeItem(SS_KEY);
  } catch (_e) { /* ignore */ }
  _listeners.forEach((fn) => { try { fn(_memToken); } catch (_e) { /* ignore */ } });
};
export const getStaffToken = () => _memToken;

// Axios instance dedicada al backoffice (usa el token staff en memoria).
export const staffApi = axios.create({ baseURL: API });
staffApi.interceptors.request.use((config) => {
  if (_memToken) config.headers.Authorization = `Bearer ${_memToken}`;
  return config;
});
staffApi.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      _setMemToken(null);
      if (typeof window !== 'undefined' &&
          !window.location.pathname.startsWith('/priviax-login')) {
        window.location.href = '/priviax-login';
      }
    }
    return Promise.reject(err);
  },
);

export function StaffAuthProvider({ children }) {
  const [token, setToken] = useState(_memToken);
  const [staffUser, setStaffUser] = useState(null);

  // Sincroniza el estado si el token cambia desde otro punto (interceptor 401).
  useMemo(() => {
    const cb = (t) => { setToken(t); if (!t) setStaffUser(null); };
    _listeners.add(cb);
    return () => _listeners.delete(cb);
  }, []);

  const requestCode = useCallback(async (email) => {
    const { data } = await staffApi.post('/auth/staff-login', { email });
    return data; // {challenge_id, expires_at, ttl_seconds}
  }, []);

  const verifyCode = useCallback(async (challenge_id, code) => {
    const { data } = await staffApi.post('/auth/staff-verify', { challenge_id, code });
    _setMemToken(data.token);
    setToken(data.token);
    setStaffUser(data.user);
    return data;
  }, []);

  const resendCode = useCallback(async (challenge_id) => {
    const { data } = await staffApi.post('/auth/staff-resend', { challenge_id });
    return data;
  }, []);

  const refresh = useCallback(async () => {
    if (!_memToken) { setStaffUser(null); return null; }
    try {
      const { data } = await staffApi.get('/auth/staff-me');
      setStaffUser(data.user);
      return data.user;
    } catch (_e) {
      _setMemToken(null);
      setStaffUser(null);
      return null;
    }
  }, []);

  const logout = useCallback(() => {
    _setMemToken(null);
    setToken(null);
    setStaffUser(null);
  }, []);

  const value = useMemo(() => ({
    token, staffUser, requestCode, verifyCode, resendCode, refresh, logout,
    isAuthed: !!token,
  }), [token, staffUser, requestCode, verifyCode, resendCode, refresh, logout]);

  return <StaffAuthContext.Provider value={value}>{children}</StaffAuthContext.Provider>;
}

export const useStaffAuth = () => {
  const ctx = useContext(StaffAuthContext);
  if (!ctx) throw new Error('useStaffAuth must be used within StaffAuthProvider');
  return ctx;
};
